<script setup lang="ts">
import AppConfirm from './components/shared/app-confirm.vue';
import { onBeforeUnmount, watch, onMounted, nextTick, ref, computed } from 'vue';
import { onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from './api/auth/auth.api';
import { disconnectSocket, initSocket } from './services/socket.service';
import { useQueryClient } from '@tanstack/vue-query';
import {
  shouldRequestPushPermission,
  useEnablePushNotifications,
  usePushPermissionState,
} from './services/push-notifications';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { useUser } from '@/api/user/queries/use-user';
import { useI18n } from 'vue-i18n';
import { notificationsOutline } from 'ionicons/icons';
import { Capacitor, type PermissionState } from '@capacitor/core';
import { useAlertEvents } from '@/services/use-alert-events';
import { useRouterInTab } from './hooks/use-router-in-tab';
import { RouteName } from '@/router';
import { useRouter } from 'vue-router';
import { Keyboard } from '@capacitor/keyboard';
import { getAppVersionStatus } from './api/auth/auth.api';
import AppForceUpdate from '@/components/shared/app-force-update.vue';
import PrivacyConsentPrompt from '@/components/shared/privacy-consent-prompt.vue';
import WorkoutRecoveryModal from '@/views/workout-player/components/workout-recovery-modal.vue';
import WorkoutEarlyExitModal from '@/views/workout-player/components/workout-early-exit-modal.vue';
import { useBackButton } from '@ionic/vue';
import { navigateToStore, shouldNavigateToStore } from './services/navigate-to-store';
import {
  isPublicAuthEntryPath,
  parseIncomingAppUrl,
  storePendingDeepLink,
  withExternalPostViewEntry,
  resolveColdStartDeepLinkUrl,
} from './services/deep-link';
import { markPushNavigationReady } from './services/pending-push-navigation';
import { usePushAlertClick } from './services/use-alert-click';
import { SplashScreen } from '@capacitor/splash-screen';
import { QueryKey } from './api/common/types/query-key.enum';
import { prefetchGlobalLeaderboardPreview } from './api/leaderboard/queries/use-leaderboard';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import logger from './services/logger';
import { logAppOpen } from './services/analytics';
import { onMetaAppActive } from './services/meta-app-events';
import { useWorkoutRecovery } from '@/composables/use-workout-recovery';
import { useAgeGate } from '@/composables/use-age-gate';
import { runLiveUpdateStartup } from './services/live-update-startup';
import { clearSplashFailsafe } from './services/splash-failsafe';
import environment from './environment';
import { useAuthState } from '@/state/user';

const { isBackendUserProvisioned, setBackendUserProvisioned } = useAuthState();

const { t } = useI18n();
useAlertEvents();
const onPushAlertClick = usePushAlertClick();
const { data: pushPermissionState } = usePushPermissionState();
const { mutateAsync: enablePushNotifications } = useEnablePushNotifications();
/** True once startup Live Update gate finished and the UI may be shown. */
const isAppShellReady = ref(false);
const isForceUpdateRequired = ref(false);
const { data: user } = useUser();
const isPushPermissionOpen = ref(false);
const queryClient = useQueryClient();
const routerInTab = useRouterInTab();
const router = useRouter();
const leftAppTime = ref<number | null>(null);
let appStateListener: Awaited<ReturnType<typeof App.addListener>>;

const userReady = computed(() => !!user.value && !!firebaseAuth.currentUser);
const {
  open: recoveryOpen,
  earlyExitOpen: recoveryEarlyExitOpen,
  workout: recoveryWorkout,
  busy: recoveryBusy,
  earlyExitSubmitting: recoveryEarlyExitSubmitting,
  onResume: onRecoveryResume,
  onFinishNow: onRecoveryFinishNow,
  onDiscard: onRecoveryDiscard,
  onEarlyExitClose: onRecoveryEarlyExitClose,
  onEarlyExitContinue: onRecoveryEarlyExitContinue,
  onEarlyExitSubmit: onRecoveryEarlyExitSubmit,
  onEarlyExitQuit: onRecoveryEarlyExitQuit,
  dismissForSession: onRecoveryDismiss,
} = useWorkoutRecovery(userReady);
// Missing date of birth (asked once) and under-13 accounts; Meta / Replay follow date changes.
useAgeGate();
const lastOpenedDeepLink = ref<string | null>(null);
const pendingAppUrl = ref<string | null>(null);

const applyIncomingAppUrl = async (rawUrl: string) => {
  const parsed = parseIncomingAppUrl(rawUrl);
  if (!parsed) return;
  if (lastOpenedDeepLink.value === parsed.fullPath) return;
  lastOpenedDeepLink.value = parsed.fullPath;

  if (isPublicAuthEntryPath(parsed.path)) {
    // The route guard sends signed-in users on to the app.
    await router.replace({ path: parsed.path, query: parsed.query });
    return;
  }

  await firebaseAuth.authStateReady();
  if (!firebaseAuth.currentUser) {
    storePendingDeepLink(parsed.fullPath);
    await router.replace({ path: `/${RouteName.Login}`, query: { redirect: parsed.fullPath } });
    return;
  }

  if (parsed.postId) {
    const marked = withExternalPostViewEntry(parsed.path, parsed.query);
    await routerInTab.navigate(marked.fullPath, 'root', 'replace');
    await nextTick();
    const currentPath = router.currentRoute.value.path;
    if (!currentPath.includes(`/post-view/${parsed.postId}`)) {
      await router.replace({ path: marked.path, query: marked.query });
    }
    return;
  }

  await router.replace({ path: parsed.path, query: parsed.query });
};

const handleIncomingAppUrl = async (rawUrl: string) => {
  if (!isAppShellReady.value) {
    pendingAppUrl.value = rawUrl;
    return;
  }
  await applyIncomingAppUrl(rawUrl);
};

const flushQueuedDeepLink = async () => {
  await nextTick();
  await nextTick();
  try {
    await router.isReady();
  } catch {
    // Router may already be ready.
  }
  let launchUrl: string | null = null;
  try {
    const result = await App.getLaunchUrl();
    launchUrl = result?.url ?? null;
  } catch {
    // Launch URL is optional on web / unsupported platforms.
  }
  const url = resolveColdStartDeepLinkUrl(pendingAppUrl.value, launchUrl);
  pendingAppUrl.value = null;
  if (url) await applyIncomingAppUrl(url);
};

const appUrlOpenListener = App.addListener('appUrlOpen', function (event: URLOpenListenerEvent) {
  void handleIncomingAppUrl(event.url);
});
const requestPushPermission = async (permissionState: PermissionState | undefined) => {
  if (!permissionState) return;
  if (permissionState == 'granted') {
    enablePushNotifications();
  } else {
    isPushPermissionOpen.value = await shouldRequestPushPermission();
  }
};
const authListener = onAuthStateChanged(firebaseAuth, user => {
  if (!user) setBackendUserProvisioned(false);
});

// Being signed in to Firebase is not enough to start the app. Each call below
// hits an endpoint that 401s until the GYMing user exists, and that 401 forces
// a logout — which is what used to kill signup half way through.
watch(
  isBackendUserProvisioned,
  provisioned => {
    if (!provisioned) return;
    prefetchGlobalLeaderboardPreview(queryClient);
    initSocket(queryClient, routerInTab);
    if (!isPushPermissionOpen.value) {
      requestPushPermission(pushPermissionState.value);
    }
  },
  { immediate: true }
);

/**
 * Runs after the shell is on screen. A new bundle is armed for the next cold
 * start rather than reloaded now, so this never disturbs the visible app; the
 * only thing it can surface is the force-update wall, which is an overlay.
 */
const checkForLiveUpdateInBackground = async () => {
  try {
    const result = await runLiveUpdateStartup({
      ready: () => LiveUpdate.ready(),
      getUpdateStatus: getAppVersionStatus,
      getBundles: () => LiveUpdate.getBundles(),
      downloadBundle: opts => LiveUpdate.downloadBundle(opts),
      setNextBundle: opts => LiveUpdate.setNextBundle(opts),
      buildDownloadUrl: version => `${environment.liveUpdateBaseUrl}/dist-${version}.zip`,
    });

    if (result.action === 'force_update') {
      isForceUpdateRequired.value = true;
    }
  } catch (error) {
    logger.error({
      message: 'LiveUpdate background check failed',
      data: {},
      error: error instanceof Error ? error : new Error(String(error)),
    });
  }
};

onMounted(async () => {
  const revealApp = async () => {
    isAppShellReady.value = true;
    clearSplashFailsafe();
    try {
      await SplashScreen.hide();
    } catch {
      // Ignore — failsafe also releases splash if still armed.
    }
  };

  // First paint must not wait on the Live Update check. It used to, and on a cold
  // start that check (two network round trips, plus a bundle download after a
  // release) left the user on a flat #202020 window with no router outlet mounted.
  await revealApp();

  // DEV (Vite live-reload) must not download an OTA bundle over the local frontend.
  if (Capacitor.isNativePlatform() && !import.meta.env.DEV) {
    void checkForLiveUpdateInBackground();
  }

  if (
    shouldNavigateToStore(router.currentRoute?.value?.query?.debug as string | undefined, {
      platform: Capacitor.getPlatform(),
      environmentName: environment.name,
      path: router.currentRoute?.value?.path,
      debugFlag: false,
    })
  ) {
    navigateToStore();
  }

  await flushQueuedDeepLink();

  appStateListener = await App.addListener('appStateChange', async ({ isActive }) => {
    try {
      if (isActive) {
        await logAppOpen();
        void onMetaAppActive();
        if (Capacitor.isNativePlatform()) {
          initSocket(queryClient, routerInTab);
        }
        // If left app for more than 30 seconds, invalidate queries
        if (leftAppTime.value && Date.now() - leftAppTime.value > 1000 * 30) {
          try {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [QueryKey.GetAlerts] }),
              queryClient.invalidateQueries({ queryKey: [QueryKey.GetStories] }),
              queryClient.invalidateQueries({ queryKey: [QueryKey.GetChats] }),
              queryClient.invalidateQueries({ queryKey: [QueryKey.GetLoggedInUser] }),
            ]);
          } catch (queryError) {
            logger.error({
              message: 'Failed to invalidate queries on app resume',
              data: {},
              error: queryError instanceof Error ? queryError : new Error(String(queryError)),
            });
          }
        }
        leftAppTime.value = null;
      } else {
        if (Capacitor.isNativePlatform()) {
          leftAppTime.value = Date.now();
          disconnectSocket();
          try {
            await Keyboard.hide();
          } catch {
            // Ignore keyboard errors - not critical
          }
        }
      }
    } catch (error) {
      logger.error({
        message: 'App state change handler failed',
        data: { isActive },
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  });
});

useBackButton(1, () => {
  // The home (feed) tab is the app root: there is nothing logically before it,
  // so back exits the app (standard Android behavior). We intentionally don't
  // rely on canGoBack() here since Ionic's history position isn't reset by a
  // root navigation, which would otherwise let back walk a stale stack.
  if (Capacitor.isNativePlatform() && router.currentRoute.value.path === `/${RouteName.App}/${RouteName.Feed}`) {
    App.exitApp();
    return;
  }
  routerInTab.back();
});

onBeforeUnmount(() => {
  authListener();
  if (appStateListener) appStateListener.remove();
  appUrlOpenListener.then(listener => listener.remove());
});

watch(isAppShellReady, async ready => {
  if (!ready) return;
  await nextTick();
  markPushNavigationReady(onPushAlertClick);
});

watch(pushPermissionState, async newPushPermissionState => {
  if (!firebaseAuth.currentUser || isPushPermissionOpen.value) return;
  requestPushPermission(newPushPermissionState);
});

const confirmData = computed(() => {
  return {
    icon: notificationsOutline,
    title: t('confirm-push-alerts.title'),
    subtitle: t('confirm-push-alerts.subtitle'),
    btnLabel: t('confirm-push-alerts.btnLabel'),
    cancelBtnLabel: t('confirm-push-alerts.cancelBtnLabel'),
    color: 'primary',
    onConfirm: async () => {
      isPushPermissionOpen.value = false;
      await enablePushNotifications();
    },
  };
});
</script>

<template>
  <ion-app>
    <!-- Defer homepage chrome until the splash-held Live Update gate finishes.
         Prevents the old feed from flashing under a late splash hide / before reload. -->
    <ion-router-outlet v-if="isAppShellReady" />
    <app-confirm :open="isPushPermissionOpen" :confirm-data="confirmData" @close="isPushPermissionOpen = false" />
    <workout-recovery-modal
      :open="recoveryOpen && isAppShellReady"
      :workout="recoveryWorkout"
      :busy="recoveryBusy"
      @resume="onRecoveryResume"
      @finish-now="onRecoveryFinishNow"
      @discard="onRecoveryDiscard"
      @dismiss="onRecoveryDismiss"
    />
    <workout-early-exit-modal
      :open="recoveryEarlyExitOpen && isAppShellReady"
      :submitting="recoveryEarlyExitSubmitting"
      @close="onRecoveryEarlyExitClose"
      @continue="onRecoveryEarlyExitContinue"
      @submit="onRecoveryEarlyExitSubmit"
      @quit="onRecoveryEarlyExitQuit"
    />
    <app-force-update :open="isForceUpdateRequired" />
    <privacy-consent-prompt
      :enabled="
        isAppShellReady && !isForceUpdateRequired && !isPushPermissionOpen && !recoveryOpen && !recoveryEarlyExitOpen
      "
    />
  </ion-app>
</template>
